import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {
    CampaignGroupDetailDto,
    CampaignGroupLinkedCampaignDto,
    EngineCampaignGroupService,
    EngineCampaignService
} from '@tj/tj-dmp-lib-api';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfirmationService, MessageService, SelectItem} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'dmp-campaign-group-edit',
    templateUrl: './campaign-group-edit.component.html',
    styleUrls: ['./campaign-group-edit.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class CampaignGroupEditComponent implements OnInit {

    breadcrumb = [
        {label: this.translate.instant('menu.marketing.label')},
        {label: this.translate.instant('menu.marketing.campaignGroups'), routerLink: ['/h/marketing/campaign-group']},
        {label: this.translate.instant('campaign.group.page.breadcrumbEdit')}
    ];
    description = '';
    form: UntypedFormGroup;
    actual: CampaignGroupDetailDto;
    tenant: string;
    disabled = false;
    submitted = false;
    isNew = false;
    linkedCampaigns: CampaignGroupLinkedCampaignDto[] = [];
    availableCampaignOptions: SelectItem[] = [];
    selectedCampaignIdsToAdd: number[] = [];

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly engineCampaignGroupService: EngineCampaignGroupService,
        private readonly engineCampaignService: EngineCampaignService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly messageService: MessageService,
        private readonly translate: TranslateService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        const isEdit = this.router.url.includes('/edit');
        this.breadcrumb = [
            {label: this.translate.instant('menu.marketing.label')},
            {label: this.translate.instant('menu.marketing.campaignGroups'), routerLink: ['/h/marketing/campaign-group']},
            {label: this.translate.instant('campaign.group.page.breadcrumbEdit')}
        ];
        if (this.isNew) {
            this.breadcrumb[2] = {label: this.translate.instant('campaign.group.page.breadcrumbCreate')};
            this.description = this.translate.instant('campaign.group.page.descriptionCreate');
        } else if (isEdit) {
            this.breadcrumb[2] = {label: this.translate.instant('campaign.group.page.breadcrumbEdit')};
            this.description = this.translate.instant('campaign.group.page.descriptionEdit');
        } else {
            this.breadcrumb[2] = {label: this.translate.instant('campaign.group.page.breadcrumbDetail')};
            this.description = this.translate.instant('campaign.group.page.descriptionDetail');
        }
    }

    ngOnInit(): void {
        this.isNew = this.router.url.includes('/new/');
        const isEdit = this.router.url.includes('/edit');
        this.tenant = this.route.snapshot.paramMap.get('tenant');
        this.disabled = !this.isNew && !isEdit;

        this.rebuildTranslatedHeader();

        this.form = this.fb.group({
            id: [],
            tenant: [{value: this.tenant, disabled: true}],
            partner: ['', [Validators.required]],
            code: [{value: '', disabled: this.disabled}, [Validators.required, Validators.maxLength(50)]],
            name: [{value: '', disabled: this.disabled}, [Validators.required, Validators.maxLength(255)]],
            description: [{value: '', disabled: this.disabled}]
        });

        if (this.disabled) {
            this.form.disable();
        }

        if (this.isNew) {
            const initialPartner = this.resolveInitialPartner();
            if (initialPartner) {
                this.form.patchValue({ partner: initialPartner });
                this.loadAvailableCampaigns();
            }
        } else {
            this.loadGroup();
        }

        this.form.get('partner')?.valueChanges.subscribe(() => {
            if (!this.disabled) {
                this.loadAvailableCampaigns();
            }
            this.clearServerError('partner', 'partnermismatch');
        });

        this.form.get('code')?.valueChanges.subscribe(() => {
            this.clearServerError('code', 'codeexists');
        });
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    private resolveInitialPartner(): string | null {
        const fromQuery = this.route.snapshot.queryParamMap.get('partner');
        if (fromQuery) {
            return fromQuery;
        }
        const stored = localStorage.getItem('group');
        if (!stored || !this.tenant) {
            return null;
        }
        const parts = stored.split(':');
        if (parts[0] !== this.tenant || !parts[1]) {
            return null;
        }
        return parts[1];
    }

    loadGroup(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (!id || !this.tenant) {
            return;
        }
        this.engineCampaignGroupService.get(this.tenant, parseInt(id, 10)).subscribe({
            next: (res) => {
                this.actual = res;
                this.linkedCampaigns = [...(res.campaigns || [])];
                this.form.patchValue({
                    id: res.id,
                    tenant: res.tenant || this.tenant,
                    partner: res.partner,
                    code: res.code,
                    name: res.name,
                    description: res.description
                }, {emitEvent: false});
                if (!this.disabled) {
                    this.loadAvailableCampaigns();
                }
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('campaign.edit.message.errorSummary'),
                    detail: this.translate.instant('campaignGroup.notFoundDetail')
                });
                this.router.navigate(['/h/marketing/campaign-group']);
            }
        });
    }

    onDisabledChange(): void {
        if (!this.actual?.id) {
            return;
        }
        this.router.navigate(['/h/marketing/campaign-group', this.tenant, this.actual.id, 'edit']);
    }

    loadAvailableCampaigns(): void {
        const partner = this.form.get('partner')?.value;
        if (!this.tenant || !partner) {
            this.availableCampaignOptions = [];
            return;
        }
        this.engineCampaignService.findAll(this.tenant, partner).subscribe({
            next: (campaigns) => {
                const linkedIds = new Set(this.linkedCampaigns.map(c => c.id));
                this.availableCampaignOptions = (campaigns || [])
                    .filter(c => !linkedIds.has(c.id))
                    .map(c => ({
                        label: `${c.code} - ${c.name}`,
                        value: c.id
                    }));
            }
        });
    }

    addSelectedCampaigns(): void {
        if (!this.selectedCampaignIdsToAdd?.length) {
            return;
        }
        const partner = this.form.get('partner')?.value;
        this.engineCampaignService.findAll(this.tenant, partner).subscribe({
            next: (campaigns) => {
                const toAdd = (campaigns || []).filter(c => this.selectedCampaignIdsToAdd.includes(c.id));
                toAdd.forEach(c => {
                    if (!this.linkedCampaigns.some(lc => lc.id === c.id)) {
                        this.linkedCampaigns.push({
                            id: c.id,
                            code: c.code,
                            name: c.name,
                            partner: c.partner
                        });
                    }
                });
                this.selectedCampaignIdsToAdd = [];
                this.loadAvailableCampaigns();
            }
        });
    }

    removeCampaign(campaign: CampaignGroupLinkedCampaignDto): void {
        this.linkedCampaigns = this.linkedCampaigns.filter(c => c.id !== campaign.id);
        this.loadAvailableCampaigns();
    }

    submit(): void {
        this.submitted = true;
        if (this.form.invalid) {
            return;
        }

        const payload: CampaignGroupDetailDto = {
            ...this.form.getRawValue(),
            tenant: this.tenant,
            campaignIds: this.linkedCampaigns.map(c => c.id),
            campaigns: this.linkedCampaigns
        };

        if (this.isNew) {
            this.engineCampaignGroupService.create(this.tenant, payload).subscribe({
                next: (res) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('campaign.edit.message.successSummary'),
                        detail: this.translate.instant('campaignGroup.createdDetail')
                    });
                    this.router.navigate(['/h/marketing/campaign-group', this.tenant, res.id]);
                },
                error: (err) => this.handleSubmitError(err, this.translate.instant('campaignGroup.createFailedDetail'))
            });
            return;
        }

        this.engineCampaignGroupService.update(this.tenant, payload.id, payload).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('campaign.edit.message.successSummary'),
                    detail: this.translate.instant('campaignGroup.savedDetail')
                });
                this.router.navigate(['/h/marketing/campaign-group', this.tenant, payload.id]);
            },
            error: (err) => this.handleSubmitError(err, this.translate.instant('campaignGroup.saveFailedDetail'))
        });
    }

    cancel(): void {
        if (this.isNew) {
            this.router.navigate(['/h/marketing/campaign-group']);
            return;
        }
        this.router.navigate(['/h/marketing/campaign-group', this.tenant, this.actual?.id]);
    }

    private handleSubmitError(err: unknown, fallbackMessage: string): void {
        this.submitted = true;
        const errorKey = this.resolveErrorKey(err);
        this.applyFieldError(errorKey);

        if (!errorKey) {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('campaign.edit.message.errorSummary'),
                detail: fallbackMessage
            });
        }
    }

    private resolveErrorKey(err: unknown): string | null {
        const body = this.resolveApiErrorBody(err);
        if (body?.errorKey) {
            return body.errorKey;
        }
        if (typeof err === 'string') {
            if (err.includes('codeexists')) {
                return 'codeexists';
            }
            if (err.includes('partnermismatch')) {
                return 'partnermismatch';
            }
        }
        return null;
    }

    private resolveApiErrorBody(err: unknown): { errorKey?: string; entityName?: string } | null {
        if (!err || typeof err !== 'object') {
            return null;
        }
        const httpErr = err as HttpErrorResponse;
        if (httpErr.error && typeof httpErr.error === 'object' && httpErr.error.errorKey) {
            return httpErr.error;
        }
        if ((err as { errorKey?: string }).errorKey) {
            return err as { errorKey?: string };
        }
        return null;
    }

    private applyFieldError(errorKey: string | null): void {
        if (errorKey === 'codeexists') {
            this.setServerError('code', 'codeexists');
        } else if (errorKey === 'partnermismatch') {
            this.setServerError('partner', 'partnermismatch');
        }
    }

    private setServerError(fieldName: string, errorName: string): void {
        const control = this.form.get(fieldName);
        if (!control) {
            return;
        }
        control.setErrors({...(control.errors || {}), [errorName]: true});
        control.markAsTouched();
        control.markAsDirty();
    }

    private clearServerError(fieldName: string, errorName: string): void {
        const control = this.form.get(fieldName);
        if (!control?.errors?.[errorName]) {
            return;
        }
        const {[errorName]: _removed, ...remainingErrors} = control.errors;
        control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
    }
}
